/**
 * Convert a #RRGGBB hex literal to the "H S% L%" triplet used by tokens.css.
 * Returns null for malformed input so callers can fall back to the default.
 *
 * Example: hexToHslTriplet('#0072CE') === '211 100% 40%'
 */
export function hexToHslTriplet(hex: string): string | null {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim())
  if (!m) return null
  const int = parseInt(m[1], 16)
  const r = ((int >> 16) & 0xff) / 255
  const g = ((int >> 8) & 0xff) / 255
  const b = (int & 0xff) / 255
  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  const l = (max + min) / 2
  let h = 0
  let s = 0
  if (max !== min) {
    const d = max - min
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break
      case g: h = (b - r) / d + 2; break
      case b: h = (r - g) / d + 4; break
    }
    h /= 6
  }
  const H = Math.round(h * 360)
  const S = Math.round(s * 100)
  const L = Math.round(l * 100)
  return `${H} ${S}% ${L}%`
}

/**
 * Build the three-stop primary palette from a single hex. The hover stop
 * darkens by ~6 lightness points; the strong stop (used for hero tiles)
 * darkens by ~18, matching the relationship between the default Bokapsys
 * blue tokens (#0072CE → #0058a8 → #003a73).
 */
export function deriveBrandPalette(hex: string): {
  primary: string
  hover: string
  strong: string
  accent: string
  accentFg: string
} | null {
  const base = parseHsl(hex)
  if (!base) return null
  const clampL = (n: number) => Math.max(0, Math.min(100, n))
  const fmt = (h: number, s: number, l: number) => `${h} ${s}% ${clampL(l)}%`
  return {
    primary: fmt(base.h, base.s, base.l),
    hover: fmt(base.h, base.s, base.l - 6),
    strong: fmt(base.h, base.s, Math.max(18, base.l - 18)),
    // Pale wash used for sidebar hover/active and chips. Keep hue/sat,
    // push lightness near-white.
    accent: fmt(base.h, Math.min(100, base.s), 95),
    accentFg: fmt(base.h, base.s, Math.max(20, base.l - 12)),
  }
}

function parseHsl(hex: string): { h: number; s: number; l: number } | null {
  const triplet = hexToHslTriplet(hex)
  if (!triplet) return null
  const m = /^(\d+)\s+(\d+)%\s+(\d+)%$/.exec(triplet)
  if (!m) return null
  return { h: +m[1], s: +m[2], l: +m[3] }
}

/**
 * Dark-mode variant of `deriveBrandPalette`. Only emits `primary` +
 * `hover` because in dark mode we deliberately let the dark theme own
 * the accent/strong stops (bg-accent in dark mode is a near-black
 * wash, not a near-white one - overriding it from a light-mode brand
 * derivation would wreck the contrast).
 *
 * The lightness is floored at 55% so a dark brand like `#15803d`
 * (forest emerald, L=28%) still reads as a button on the dark canvas.
 * Lighter brands pass through unchanged.
 */
export function deriveBrandPaletteForDark(hex: string): {
  primary: string
  hover: string
} | null {
  const base = parseHsl(hex)
  if (!base) return null
  const l = Math.max(base.l, 55)
  const clampL = (n: number) => Math.max(0, Math.min(100, n))
  const fmt = (h: number, s: number, lightness: number) =>
    `${h} ${s}% ${clampL(lightness)}%`
  return {
    primary: fmt(base.h, base.s, l),
    hover: fmt(base.h, base.s, Math.min(85, l + 8)),
  }
}
